import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { Button,  ThemeProviderV2} from '@blueprint/ui';
import { useModal } from "react-modal-hook";

import usersApi, { useUserById } from "../../../api/users";
import { UserStatus } from "../../../types/users";
import { Loader, toast } from "../../../common/components/core";
import { useAuthErrorHandlerCreator } from "../../../common/hooks";
import { useGeneralErrorHandlerCreator } from "../../../common/hooks/useGeneralErrorHandler";
import {
    GenericError,
    composeErrorHandlers,
} from "../../../utils/errorHandling";


type ReviewApproveHandler = {
    isSubmitting: boolean;
    handler: (
        name: string,
        status: UserStatus,
    ) => void;
};

const useReviewApproveHandler = (userId: string): ReviewApproveHandler => {
    const [ isSubmitting, setSubmitting ] = useState(false);
    const [ name, setName ] = useState<string>("");
    const [ status, setStatus ] = useState<UserStatus>();
    const onAuthError = useAuthErrorHandlerCreator();
    const useGeneralError = useGeneralErrorHandlerCreator();
    const { mutate } = useUserById(userId);
    const typeText = status === UserStatus.PENDING_CREATE_APPROVAL ? "Creation" : "Edit";
    const typeTextLow = status === UserStatus.PENDING_CREATE_APPROVAL ? "creation" : "edit";

    const [show, hide] = useModal(() => {
        const confirm = async () => {
            setSubmitting(true);
            try {
                await usersApi.review(userId, 0, null);
                toast(`User ${name} ${typeTextLow} approved`);
                mutate();
            } catch (e) {
                composeErrorHandlers(
                    onAuthError,
                    useGeneralError,
                )(e as GenericError);
            }
            hide();
            setSubmitting(false);
        };

        return (
            <ThemeProviderV2>
            <Modal show onHide={hide} backdrop="static" centered>
                <Loader overlay isLoading={isSubmitting}>
                    <Modal.Header>
                        <h2 className="mb-0">
                            {`Approve User ${typeText}`}
                        </h2>
                    </Modal.Header>
                    <Modal.Body>
                        <p>{`Are you sure you want to approve this user ${typeTextLow}?`}</p>
                    </Modal.Body>

                    <Modal.Footer className="d-flex justify-content-between px-3">
                        <Button variant="light.tertiary" onClick={hide}>
                            Cancel
                        </Button>
                        <Button variant="light.primary" onClick={confirm}>
                            Confirm
                        </Button>
                    </Modal.Footer>
                </Loader>
            </Modal>
            </ThemeProviderV2>
        );
    }, [ userId, isSubmitting, name, status, onAuthError]);

    return {
        isSubmitting,
        handler: (name, status) => {
            setName(name);
            setStatus(status);
            show();
        },
    };
};

export default useReviewApproveHandler;
